import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Plug, RefreshCw, ShieldCheck, AlertTriangle, FileText, Shield, Settings, Store } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useSuperAdminAuth } from '../../contexts/SuperAdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// EMT SuperAdmin (2026-06-17): PLATFORM connector-framework governance — aggregate
// adoption/health only, from GET /super-admin/integration-governance/health.
// This is NOT the Business CRM tenant Settings → Integrations. It never shows or
// configures tenant credentials, API keys, webhook URLs/secrets, field mappings,
// sync rules, tickets, transcripts, customer PII, or tenant payloads.

const PlatformIntegrationGovernance = () => {
  const { superAdmin } = useSuperAdminAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiRef = useRef(null);
  if (!apiRef.current) {
    const instance = axios.create({ baseURL: BACKEND_URL });
    instance.interceptors.request.use((config) => {
      const token = superAdmin?.token || localStorage.getItem('superadmin_session') ||
        localStorage.getItem('superadmin_session_token') || localStorage.getItem('superAdminToken') ||
        localStorage.getItem('superadminToken');
      if (token) { config.headers = config.headers || {}; config.headers.Authorization = `Bearer ${token}`; }
      return config;
    });
    apiRef.current = instance;
  }
  const api = apiRef.current;

  const fetchHealth = async () => {
    setLoading(true); setError(null);
    try { const r = await api.get('/api/v1/super-admin/integration-governance/health'); setData(r.data); }
    catch (err) { setError(err?.response?.status ? `HTTP ${err.response.status}` : 'network error'); setData(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchHealth(); }, []);

  const connectors = data?.connectors || [];
  const readinessBadge = (r) => r === 'available'
    ? <Badge className="bg-green-100 text-green-800">Available</Badge>
    : <Badge className="bg-gray-100 text-gray-600">Planned</Badge>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Platform Integration Governance</h1>
          <p className="text-gray-600 mt-1">Global connector frameworks &amp; aggregate platform health{data ? ` · ${data.total_tenants} tenants` : ''}</p>
        </div>
        <Button onClick={fetchHealth} disabled={loading}><RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh Health</Button>
      </div>

      <div className="flex items-start gap-3 p-3 rounded-lg bg-purple-50 border border-purple-200">
        <ShieldCheck className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-purple-800">
          Platform-level connector framework governance — aggregate adoption/readiness only.
          Tenant credentials, webhook secrets, field mappings, sync rules and payloads are managed by each
          tenant under Business CRM → Settings → Integrations and are never shown here.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-3 p-3 rounded-lg bg-red-50 border border-red-200">
          <AlertTriangle className="h-5 w-5 text-red-600" />
          <p className="text-sm text-red-700">Couldn't load integration health ({error}).</p>
          <Button onClick={fetchHealth} size="sm" variant="outline" className="ml-auto">Retry</Button>
        </div>
      )}

      <Card className="bg-white shadow-md">
        <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-lg"><Plug className="h-5 w-5 text-purple-600" />Connector Framework Catalog &amp; Adoption</CardTitle></CardHeader>
        <CardContent>
          {loading ? <p className="text-sm text-gray-400">Loading…</p>
            : connectors.length === 0 ? <p className="text-sm text-gray-500">No connector frameworks reported.</p>
            : (
              <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead className="bg-gray-50 border-b"><tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Connector</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Readiness</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Tenants Enabled</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Adoption</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {connectors.map((c, i) => (
                    <tr key={c.name || i} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium text-gray-900 capitalize">{c.name}</td>
                      <td className="px-4 py-2">{readinessBadge(c.readiness)}</td>
                      <td className="px-4 py-2 text-gray-700">{c.tenants_enabled}</td>
                      <td className="px-4 py-2 text-gray-700">{c.adoption_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
        </CardContent>
      </Card>

      {/* Working navigation to related platform-governance pages */}
      <Card className="bg-white shadow-md">
        <CardHeader className="pb-3"><CardTitle className="text-lg">Related Governance</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={() => navigate('/superadmin-portal/dashboard/audit-logs')}><FileText className="h-4 w-4 mr-2" />Audit Logs</Button>
            <Button variant="outline" onClick={() => navigate('/superadmin-portal/dashboard/security')}><Shield className="h-4 w-4 mr-2" />Security &amp; Compliance</Button>
            <Button variant="outline" onClick={() => navigate('/superadmin-portal/dashboard/settings')}><Settings className="h-4 w-4 mr-2" />Platform Settings</Button>
            <Button variant="outline" onClick={() => navigate('/superadmin-portal/dashboard/marketplace')}><Store className="h-4 w-4 mr-2" />Marketplace Control</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PlatformIntegrationGovernance;
